import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  cleanSessionCodeInput,
  isValidSessionCode,
  MAX_DISPLAY_NAME_LENGTH,
  SESSION_CODE_LENGTH,
  type NameMode,
  type ServerMessage,
} from "@binogi-livequiz/shared";
import Wordmark from "../components/Wordmark";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { api, ApiError } from "../lib/api";
import { serverErrorKey } from "../lib/i18n";
import { useWebSocket } from "../lib/ws-context";
import {
  loadStudentSession,
  saveStudentSession,
} from "../lib/session-storage";

type Phase = "enterCode" | "checking" | "ready" | "joining";

export default function Join() {
  const navigate = useNavigate();
  const params = useParams<{ sessionCode?: string }>();
  const ws = useWebSocket();
  const { send } = ws;
  const { t } = useTranslation();
  const initial = (params.sessionCode ?? "").toUpperCase();
  const [code, setCode] = useState(initial);
  const [nameMode, setNameMode] = useState<NameMode | null>(null);
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
  const [displayName, setDisplayName] = useState("");
  // An i18n key, translated at render so a language switch re-renders it.
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>(initial.length === SESSION_CODE_LENGTH ? "checking" : "enterCode");
  const nameInputRef = useRef<HTMLInputElement>(null);

  const codeReady = useMemo(
    () => isValidSessionCode(code.toUpperCase()),
    [code],
  );

  // Look up session metadata once per fully entered code. In character
  // mode we fire JOIN_SESSION immediately so QR-scan students skip the
  // extra tap. Depends on the stable `send`, not the whole `ws` value: that
  // changes on every socket event, which re-ran this lookup, wiped the
  // server's error and re-sent the join.
  useEffect(() => {
    if (!codeReady) {
      setPhase("enterCode");
      setNameMode(null);
      return;
    }
    let cancelled = false;
    setPhase("checking");
    setError(null);
    api
      .getSession(code.toUpperCase())
      .then((res) => {
        if (cancelled) return;
        setNameMode(res.nameMode);
        setAvailableLanguages(res.availableLanguages ?? []);
        if (res.nameMode === "character") {
          if (sentJoinRef.current) return;
          sentJoinRef.current = true;
          setPhase("joining");
          // Rejoin support: if we have a saved character for this session,
          // pass it through so the server reuses the existing PLAYER row
          // instead of assigning a brand-new character name. The saved
          // `playerToken` (when we have one for *this* session) proves we
          // own that row, so the server hands it back even if the dead
          // socket still looks alive to API Gateway.
          const loaded = loadStudentSession();
          const saved =
            loaded && loaded.sessionCode === code.toUpperCase() ? loaded : null;
          const reattachName =
            saved?.nameMode === "character" ? saved.displayName : undefined;
          const reattachToken = saved?.playerToken;
          send({
            type: "JOIN_SESSION",
            sessionCode: code.toUpperCase(),
            ...(reattachName ? { displayName: reattachName } : {}),
            ...(reattachToken ? { playerToken: reattachToken } : {}),
          });
          return;
        }
        setPhase("ready");
        setTimeout(() => nameInputRef.current?.focus(), 50);
      })
      .catch((err) => {
        if (cancelled) return;
        setNameMode(null);
        setPhase("enterCode");
        setError(
          err instanceof ApiError && err.status === 404
            ? "join.error_not_found"
            : "join.error_lookup_failed",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [code, codeReady, send]);

  // Watch for SESSION_JOINED to navigate forward.
  useEffect(() => {
    const m = ws.lastMessage;
    if (!m) return;
    handle(m);
    function handle(msg: ServerMessage) {
      if (msg.type === "SESSION_JOINED") {
        ws.consume();
        // Keep the token we already hold when the ack arrives without one
        // (matches useStudentRejoin's merge) — a full replace would wipe
        // the proof of ownership and drop us back on the liveness probe.
        const prev = loadStudentSession();
        saveStudentSession({
          sessionCode: msg.sessionCode,
          displayName: msg.displayName,
          playerToken:
            msg.playerToken ??
            (prev?.sessionCode === msg.sessionCode
              ? prev.playerToken
              : undefined),
          nameMode: msg.nameMode,
          languageMode: msg.languageMode,
          availableLanguages: msg.availableLanguages,
        });
        navigate("/play/waiting", {
          state: {
            sessionCode: msg.sessionCode,
            displayName: msg.displayName,
            playerCount: msg.playerCount,
            nameMode: msg.nameMode,
            expectedStudents: msg.expectedStudents,
            availableLanguages: msg.availableLanguages,
          },
        });
      } else if (msg.type === "ERROR") {
        ws.consume();
        // Re-arm the join guard: an ERROR ends this attempt, and the next
        // tap must actually send (NAME_TAKEN in free mode means "type
        // another name and try again" — without the reset the button just
        // cleared the error and sent nothing).
        sentJoinRef.current = false;
        setPhase("ready");
        setError(serverErrorKey(msg));
      }
    }
  }, [navigate, ws]);

  const handleCodeChange = useCallback((raw: string) => {
    setCode(cleanSessionCodeInput(raw));
    setError(null);
    // A new code is a new join attempt — re-arm the guard so character
    // mode's auto-join can fire again for the re-entered code.
    sentJoinRef.current = false;
  }, []);

  const sentJoinRef = useRef(false);
  const handleJoin = useCallback(() => {
    setError(null);
    if (sentJoinRef.current) return;
    if (!codeReady) {
      setError("join.error_code_required");
      return;
    }
    if (nameMode === "free" && !displayName.trim()) {
      setError("join.error_name_required");
      nameInputRef.current?.focus();
      return;
    }
    sentJoinRef.current = true;
    setPhase("joining");
    const upperCode = code.toUpperCase();
    const trimmedName = displayName.trim();
    // Carry the saved `playerToken` on a manual join too — without it a
    // returning free-mode student who retypes their name lands on the
    // liveness probe and gets NAME_TAKEN from their own half-open ghost.
    // The token proves ownership of *that specific name*, so only send it
    // when the typed name matches the saved one (character mode has no
    // typed name, so the saved name is by definition the one we want back).
    const loaded = loadStudentSession();
    const saved =
      loaded &&
      loaded.sessionCode === upperCode &&
      (nameMode !== "free" || loaded.displayName === trimmedName)
        ? loaded
        : null;
    ws.send({
      type: "JOIN_SESSION",
      sessionCode: upperCode,
      // Character mode has no typed name, but the server's token path
      // needs displayName + playerToken together — a token without a name
      // is dead weight and the student gets re-rolled at score 0.
      ...(nameMode === "free"
        ? { displayName: trimmedName }
        : saved
          ? { displayName: saved.displayName }
          : {}),
      ...(saved?.playerToken ? { playerToken: saved.playerToken } : {}),
    });
  }, [code, codeReady, displayName, nameMode, ws]);

  // NAME_TAKEN in character mode means the server found no free character
  // this time, not that the student picked a taken name.
  const errorKey =
    error === "errors.name_taken" && nameMode === "character"
      ? "errors.character_busy"
      : error;

  return (
    <main className="min-h-full flex flex-col items-center justify-center px-6 py-10 relative">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher
          available={availableLanguages.length > 0 ? availableLanguages : undefined}
        />
      </div>
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <Wordmark size="lg" />
        </div>

        <label className="block text-sm text-white/70 mb-2 text-center">
          {t("join.code_label")}
        </label>
        <input
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          value={code}
          onChange={(e) => handleCodeChange(e.target.value)}
          placeholder="ABCDEF"
          className="w-full bg-brand-ink2 text-center text-3xl md:text-4xl font-extrabold tracking-[0.4em] py-4 rounded-2xl outline-none focus:ring-2 focus:ring-brand-teal placeholder:text-white/20"
        />

        {phase === "checking" && (
          <p className="mt-3 text-sm text-white/60 text-center">
            {t("join.checking")}
          </p>
        )}

        {phase === "ready" && nameMode === "free" && (
          <div className="mt-6">
            <label className="block text-sm text-white/70 mb-2 text-center">
              {t("join.name_label")}
            </label>
            <input
              ref={nameInputRef}
              type="text"
              autoComplete="off"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value.slice(0, MAX_DISPLAY_NAME_LENGTH))}
              placeholder={t("join.name_placeholder")}
              className="w-full bg-brand-ink2 text-center text-lg font-semibold py-3 rounded-xl outline-none focus:ring-2 focus:ring-brand-purple placeholder:text-white/20"
              onKeyDown={(e) => e.key === "Enter" && handleJoin()}
            />
          </div>
        )}

        {phase === "ready" && nameMode === "character" && (
          <p className="mt-4 text-sm text-white/60 text-center">
            {t("join.character_hint")}
          </p>
        )}

        <button
          type="button"
          onClick={handleJoin}
          disabled={phase !== "ready" || (nameMode === "free" && !displayName.trim())}
          className="w-full mt-6 bg-brand-purple text-white font-bold py-4 rounded-2xl text-lg hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {phase === "joining" ? t("join.joining") : t("join.join_button")}
        </button>

        {errorKey && (
          <p className="mt-4 text-sm text-brand-bad text-center">{t(errorKey)}</p>
        )}
      </div>
    </main>
  );
}
